import { Flex, Tag, Typography } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import { StylingUtility, theme } from '../../../utility';
import type { LocationSummary } from './inventoryListTypes';

const { Text } = Typography;

function locationTagStyle(color?: string): CSSProperties {
  const base: CSSProperties = {
    margin: 0,
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1.25,
    border: 'none',
  };
  if (!color) {
    return {
      ...base,
      backgroundColor: theme.colors.box,
      color: theme.colors.black,
    };
  }
  if (['rgb(255,255,255)', '#FFFFFF', '#ffffff'].includes(color)) {
    return {
      ...base,
      backgroundColor: theme.colors.box,
      color: theme.colors.black,
    };
  }
  return {
    ...base,
    backgroundColor: StylingUtility.getLowerOpacityColor(color),
    color,
  };
}

function LocationTag({
  color,
  children,
  className,
}: {
  color?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Tag className={className} style={locationTagStyle(color)}>
      {children}
    </Tag>
  );
}

function PickableTag({ isPickingLocation }: { isPickingLocation?: boolean }) {
  if (isPickingLocation === undefined) return null;
  return (
    <LocationTag color={isPickingLocation ? theme.colors.green : theme.colors.grey}>
      {isPickingLocation ? 'Pickable' : 'Storage'}
    </LocationTag>
  );
}

type InventoryLocationTagsProps = {
  locationSummary?: LocationSummary[];
};

export function InventoryLocationTags({ locationSummary }: InventoryLocationTagsProps) {
  if (!locationSummary?.length) return null;
  if (locationSummary.length > 1) {
    return (
      <Text type="secondary" style={{ fontSize: 14 }}>
        Multiple Locations
      </Text>
    );
  }

  const loc = locationSummary[0];
  return (
    <Flex vertical gap={6} align="flex-start">
      <LocationTag color={loc?.zone?.color}>{loc?.zone?.code}</LocationTag>
      <LocationTag color={loc?.zone?.color}>{loc?.location?.code}</LocationTag>
      {loc?.pallet?.palletNumber ? (
        <LocationTag color={theme.colors.grey}>{loc.pallet.palletNumber}</LocationTag>
      ) : null}
      <PickableTag isPickingLocation={loc?.location?.isPickingLocation} />
    </Flex>
  );
}

export function InventoryLotLocationTags({ lot }: { lot: LocationSummary }) {
  return (
    <Flex vertical gap={6} align="flex-start">
      <LocationTag color={lot.zone?.color}>{lot.zone?.code}</LocationTag>
      <LocationTag color={lot.zone?.color}>{lot?.location?.code}</LocationTag>
      {lot?.pallet?.palletNumber ? (
        <LocationTag color={theme.colors.grey}>{lot.pallet.palletNumber}</LocationTag>
      ) : null}
      <PickableTag isPickingLocation={lot?.location?.isPickingLocation} />
    </Flex>
  );
}
